/**
 * The billing address and its shipping counterpart, as PAIRS — plus the one
 * copy of the wording every "same as billing" control renders.
 *
 * ⚠️ **This module is the PAIRING and the LABEL, and deliberately nothing
 * else.** It decides no payload, no refusal and no comparison. Every consumer
 * keeps its own "is billing blank" / "does shipping already match" predicates,
 * because the two that existed before this module **already disagreed**:
 * `CompanyFormModal` compares the raw strings, `ContactFormModal` compares them
 * trimmed, and `CompanyDetailPage` has to normalise the wire's `null` to `''`
 * first because it reads a record rather than a form. Folding those into one
 * helper would be a behaviour change on two shipped forms wearing a
 * refactor's clothes — the mistake this codebase already records against the
 * two discount-amount validators. **Do not "finish" this module by moving the
 * predicates in.**
 *
 * What it DOES close is the thing its own comment used to warn about and could
 * not prevent: three hand-written lists of the same ten column names, which is
 * how a control ends up copying four of the five fields. A pair added here
 * reaches every consumer, and `Record<AddressKey, …>` tables keyed off it fail
 * to compile until they are extended too.
 */
export const ADDRESS_PAIRS = [
  ['billingStreet', 'shippingStreet'],
  ['billingCity', 'shippingCity'],
  ['billingState', 'shippingState'],
  ['billingCode', 'shippingCode'],
  ['billingCountry', 'shippingCountry'],
] as const;

export type BillingAddressKey = (typeof ADDRESS_PAIRS)[number][0];
export type ShippingAddressKey = (typeof ADDRESS_PAIRS)[number][1];
export type AddressKey = BillingAddressKey | ShippingAddressKey;

/**
 * The ONE pair a COMPANY does not have — and the reason it is excluded from the
 * company subset below rather than deleted from `ADDRESS_PAIRS` above.
 *
 * ⚠️ **`ADDRESS_PAIRS` IS SHARED WITH CONTACTS, AND A CONTACT HAS NO COUNTRY
 * FOREIGN KEY.** `billingCountry` is the only country a contact has at all, so
 * removing the pair from the shared list would silently take the Country line
 * out of the contact form's "same as billing" copy — a control that copies four
 * of the five fields, with a 200 and nothing failing. **Do not remove it.**
 */
type CountryPair = readonly ['billingCountry', 'shippingCountry'];

/**
 * The address pairs that are still PAIRS on a **company**.
 *
 * A company's country of record is a foreign key (`countryId` write /
 * `country` read) and is the only thing the server derives the billing region,
 * the currency and the Xero tax and branding defaults from. Owner's decision,
 * 2026-08-31: the free-text `billingCountry` is no longer collected or
 * displayed on a company, so there is no billing country left for a company
 * surface to render, edit or copy from.
 *
 * ⚠️ **Shipping keeps its free-text country** — `shippingCountry` is untouched
 * and has no foreign key of its own. So on a company the pairing is BROKEN on
 * that one row: the shipping side survives with nothing on the billing side to
 * copy from. That is why this is a subset of the pairs and not a subset of the
 * keys; see {@link COMPANY_UNPAIRED_SHIPPING_KEYS} for the other half.
 *
 * DERIVED from `ADDRESS_PAIRS` rather than written out, so a sixth pair added
 * above reaches every company surface with no second list to remember.
 */
export type CompanyAddressPair = Exclude<(typeof ADDRESS_PAIRS)[number], CountryPair>;
export const COMPANY_ADDRESS_PAIRS: readonly CompanyAddressPair[] = ADDRESS_PAIRS.filter(
  (pair): pair is CompanyAddressPair => pair[0] !== 'billingCountry',
);

/**
 * The shipping keys a company still writes that {@link COMPANY_ADDRESS_PAIRS}
 * no longer carries, because their billing half is gone.
 *
 * ⚠️ A company surface driving itself off the pairs ALONE would silently drop
 * `shippingCountry` — a real, editable column with a real 422 path. Every
 * company surface that enumerates shipping keys must read both lists.
 */
export const COMPANY_UNPAIRED_SHIPPING_KEYS: readonly ShippingAddressKey[] = ADDRESS_PAIRS.filter(
  (pair): pair is CountryPair => pair[0] === 'billingCountry',
).map(([, shipping]) => shipping);

/** The billing keys a company still collects. `billingCountry` is not one. */
export type CompanyBillingAddressKey = CompanyAddressPair[0];
/** Every address key a company still collects — four billing, five shipping. */
export type CompanyAddressKey = CompanyBillingAddressKey | ShippingAddressKey;

/**
 * The control's wording, in one place.
 *
 * Three surfaces offer this — the company form, the contact form and the
 * company detail page — and they must not become three slightly different
 * phrases for one action. It is a LABEL, not helper text: it names what the
 * button does and explains nothing.
 */
export const SAME_AS_BILLING_LABEL = 'Same as billing';
